"use client";

import { motion, useReducedMotion } from "framer-motion";
import { Building2, MapPin, ShoppingCart, Briefcase, UserCheck, ArrowRight, Sparkles } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
export default function Audience() {
  const shouldReduceMotion = useReducedMotion();
  const audienceItems = [{
    id: "small-businesses",
    title: "UK small businesses and startups",
    icon: Building2,
    badge: "Cost-effective",
    description: "Small business owners tired of being quoted thousands per month by large SEO agencies, looking for a more personal, cost-effective approach to improving search visibility.",
    hasLink: false,
    linkText: "",
    linkTo: "",
    linkPrefix: "",
    linkSuffix: ""
  }, {
    id: "local-services",
    title: "Local service businesses",
    icon: MapPin,
    badge: "Regional Visibility",
    description: "Plumbers, electricians, solicitors, accountants, dentists, and other service providers needing local SEO to appear in local search results and outperform nearby competitors.",
    hasLink: true,
    linkText: "local SEO",
    linkTo: "/LocalSEOPage",
    linkPrefix: "Plumbers, electricians, solicitors, accountants, dentists, and other service providers needing ",
    linkSuffix: " to appear in local search results and outperform nearby competitors."
  }, {
    id: "ecommerce-stores",
    title: "Ecommerce stores",
    icon: ShoppingCart,
    badge: "Organic Growth",
    description: "Online retailers wanting to increase organic sales and reduce reliance on paid ads and PPC budgets through tailored Ecommerce SEO strategies.",
    hasLink: true,
    linkText: "Ecommerce SEO",
    linkTo: "/EcommerceSeoPage",
    linkPrefix: "Online retailers wanting to increase organic sales and reduce reliance on paid ads and PPC budgets through targeted ",
    linkSuffix: " optimisations."
  }, {
    id: "b2b-companies",
    title: "B2B companies",
    icon: Briefcase,
    badge: "Lead Generation",
    description: "Businesses with longer sales cycles looking to generate quality leads through search, using content marketing and targeted keyword approaches with B2B SEO.",
    hasLink: true,
    linkText: "B2B SEO",
    linkTo: "/B2BSeoLeadGenerationPage",
    linkPrefix: "Businesses with longer sales cycles looking to generate quality leads through search, using content marketing and targeted keyword approaches with ",
    linkSuffix: "."
  }, {
    id: "direct-consultant",
    title: "Businesses seeking SEO support that scales as they grow",
    icon: UserCheck,
    badge: "Scalable Support",
    description: "Businesses that want to start with a focused, manageable scope and expand over time as their budget, search visibility, and commercial needs increase.",
    hasLink: false,
    linkText: "",
    linkTo: "",
    linkPrefix: "",
    linkSuffix: ""
  }];
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: "easeOut"
      }
    }
  };
  return <section id="targetaudience" data-nav="light" className="relative bg-background py-20 md:py-28 text-foreground border-b border-border/60">
      <div className="container mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-14 md:mb-18">
          <Badge variant="secondary" className="mb-4 px-3 py-1 font-medium text-xs tracking-wide">
            <Sparkles className="size-3.5 text-accent mr-1" />
            Client Fit
          </Badge>
          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground mb-4">
            Who my affordable SEO services are for
          </h2>
          <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
            My affordable SEO services are designed for UK businesses that want
            professional search engine optimisation without paying agency-level
            fees:
          </p>
        </div>

        {/* Audience Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {audienceItems.map((item, i) => {
          const IconComponent = item.icon;
          const isSpanTwo = i === 3 || i === 4;
          return <motion.div key={item.id} data-index={i} variants={itemVariants} className={isSpanTwo ? "lg:col-span-1 md:col-span-1" : "col-span-1"}>
                <Card className="h-full border border-border bg-card/60 hover:bg-card hover:border-primary/30 transition-[color,background-color,border-color,box-shadow] duration-300 hover:shadow-md flex flex-col justify-between">
                  <div>
                    <CardHeader className="gap-3 pb-3">
                      <div className="flex items-center justify-between gap-2">
                        <div className="size-11 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                          <IconComponent className="size-5" />
                        </div>
                        <span className="text-xs font-medium text-muted-foreground bg-muted px-2.5 py-1 rounded-full">
                          {item.badge}
                        </span>
                      </div>
                      <CardTitle className="font-serif text-lg sm:text-xl font-semibold text-foreground pt-1 leading-snug">
                        {item.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0">
                      {item.hasLink ? <p className="text-sm text-muted-foreground leading-relaxed">
                          {item.linkPrefix}
                          <Link to={item.linkTo} className="text-primary font-medium underline underline-offset-4 hover:text-accent transition-colors">
                            {item.linkText}
                          </Link>
                          {item.linkSuffix}
                        </p> : <p className="text-sm text-muted-foreground leading-relaxed">
                          {item.description}
                        </p>}
                    </CardContent>
                  </div>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Closing Statement */}
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.3
      }} transition={{
        duration: 0.5,
        delay: shouldReduceMotion ? 0 : 0.2
      }} className="mt-12 md:mt-16 rounded-2xl bg-secondary/50 border border-border/80 p-6 sm:p-8 text-center max-w-4xl mx-auto">
          <p className="font-serif text-base sm:text-lg text-foreground font-medium mb-4 leading-relaxed">
            {"“If you want measurable improvements in your search rankings and online presence without the complexity and cost of a traditional agency, my service was built for you.”"}
          </p>
          <div className="flex items-center justify-center">
            <Link to="/ContactPage" className="inline-flex items-center text-sm font-medium text-primary hover:text-accent transition-colors group">
              <span>Get affordable, direct SEO support</span>
              <ArrowRight className="size-4 ml-1.5 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </motion.div>
      </div>
    </section>;
}